import type { ApiClient } from '../client';
import type {
  CreateVoterDataOrderRequest,
  CreateWorkOrderRequest,
  RequoteVoterDataOrderRequest,
  ResultsReportLinkDto,
  UpdateWorkOrderRequest,
  VoterDataDownloadLinkDto,
  VoterDataQueueResponse,
  WorkOrderCheckoutPreview,
  WorkOrderCheckoutRequest,
  WorkOrderCheckoutResponse,
  WorkOrderDto,
  WorkOrderImageDto,
  WorkOrderListResponse,
  WorkOrderStatus,
  WorkOrderTransitionRequest,
} from '@signalvote/shared';

/** Query params for the Work Orders jobs list. `status` filters by exact raw status. */
export interface WorkOrderListParams {
  page?: number;
  pageSize?: number;
  status?: WorkOrderStatus;
}

/** What a platform admin hands over to fulfil a Paid Voter data order. */
export interface FulfilVoterDataOrderBody {
  file: File;
  deliveredCount: number;
  /** Required when the count is more than 5% away from the quote. */
  customerContacted: boolean;
  expectedConcurrencyVersion: number;
}

/**
 * Creates Work Orders endpoints — the internal operator surface for tracked, invoiced
 * vendor work (Tier 1: text sends). Every endpoint is platform-admin gated server-side.
 * The build screen reads one Work Order (with its live recipient count); the jobs list
 * reads the campaign's Work Orders and drives the operator's hand-run transitions.
 */
export function createWorkOrdersEndpoints(client: ApiClient) {
  return {
    /**
     * Create a text-send Work Order in Draft for the selected campaign, returning the new
     * Work Order (its id is the build screen the caller navigates to). The body never carries
     * `amount` — the price is server-owned.
     * POST /api/work-orders
     */
    create: (body: CreateWorkOrderRequest) => client.post<WorkOrderDto>('/api/work-orders', body),

    /**
     * Place a Voter data order: a Draft quoted from the server's own recount of the Segment. The
     * body carries no count or price — the server's quote is what's billed.
     * POST /api/work-orders/voter-data
     */
    createVoterData: (body: CreateVoterDataOrderRequest) =>
      client.post<WorkOrderDto>('/api/work-orders/voter-data', body),

    /**
     * Re-quote a Draft Voter data order with a new Segment, at the token the caller read; the server
     * recounts and reprices it, and the result carries the new token. 409 once the Draft is checked
     * out or the token is stale.
     * PUT /api/work-orders/{id}/voter-data
     */
    requoteVoterData: (id: string, body: RequoteVoterDataOrderRequest) =>
      client.put<WorkOrderDto>(`/api/work-orders/${id}/voter-data`, body),

    /**
     * Partially update a Draft Work Order's message / send date / send time (a null or omitted
     * field is left unchanged server-side). The self-serve build screen persists edits here so
     * the typed content survives a reload and is exactly what "Submit & Pay" invoices.
     * PATCH /api/work-orders/{id}
     */
    update: (id: string, body: UpdateWorkOrderRequest) =>
      client.patch<WorkOrderDto>(`/api/work-orders/${id}`, body),

    /**
     * Get a single Work Order with its status, details, and the live recipient count.
     * GET /api/work-orders/{id}
     */
    get: (id: string) => client.get<WorkOrderDto>(`/api/work-orders/${id}`),

    /**
     * List the campaign's Work Orders, newest-first, paginated, optionally filtered by
     * raw status. The jobs-list grouping (by what each is awaiting) is derived
     * client-side off the returned status.
     * GET /api/work-orders
     */
    list: (params?: WorkOrderListParams) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.append('page', params.page.toString());
      if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.append('status', params.status);
      const queryString = searchParams.toString();
      return client.get<WorkOrderListResponse>(
        `/api/work-orders${queryString ? `?${queryString}` : ''}`,
      );
    },

    /**
     * Apply one hand-driven operator transition (mark vendor-paid, complete, cancel,
     * fail). The body's `expectedConcurrencyVersion` must match the Work Order's current
     * version or the server rejects it with 409.
     * POST /api/work-orders/{id}/transition
     */
    transition: (id: string, body: WorkOrderTransitionRequest) =>
      client.post<WorkOrderDto>(`/api/work-orders/${id}/transition`, body),

    /**
     * Check out a Draft: one card-only Stripe invoice, for a Text send's bought list, send and first
     * send's registration, or for a Voter data list on its own (re-quoted at
     * `confirmedVoterDataPriceCents`). Returns its lines, total and the PaymentIntent client secret
     * the page confirms, with the now-Invoiced order.
     * POST /api/work-orders/{id}/checkout
     */
    checkout: (id: string, body: WorkOrderCheckoutRequest) =>
      client.post<WorkOrderCheckoutResponse>(`/api/work-orders/${id}/checkout`, body),

    /**
     * The lines and total a Draft's checkout would bill now, at Stripe's prices. Reads only.
     * GET /api/work-orders/{id}/checkout-preview
     */
    checkoutPreview: (id: string) =>
      client.get<WorkOrderCheckoutPreview>(`/api/work-orders/${id}/checkout-preview`),

    /**
     * Attach (or replace) the single image on a draft Work Order — a multipart upload of
     * the MMS creative. Replacing overwrites the stored key (one image per send). Returns
     * the stored blob key, a short-lived preview URL to render immediately and the bumped token;
     * a stale `expectedConcurrencyVersion` is 409.
     * POST /api/work-orders/{id}/image
     */
    attachImage: (id: string, file: File, expectedConcurrencyVersion: number) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('expectedConcurrencyVersion', String(expectedConcurrencyVersion));
      return client.request<WorkOrderImageDto>(`/api/work-orders/${id}/image`, {
        method: 'POST',
        body: formData,
      });
    },

    /**
     * Replaces a Draft Text send's Recipient list with an uploaded CSV of phone numbers and switches
     * its source to the upload. The server cleans the numbers and returns the order with the ready
     * and removed counts.
     * POST /api/work-orders/{id}/recipients
     */
    uploadRecipients: (id: string, file: File, expectedConcurrencyVersion: number) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('expectedConcurrencyVersion', String(expectedConcurrencyVersion));
      return client.request<WorkOrderDto>(`/api/work-orders/${id}/recipients`, {
        method: 'POST',
        body: formData,
      });
    },

    /**
     * Paid Voter data orders awaiting fulfilment, across every campaign, oldest first. Platform
     * admins only.
     * GET /api/work-orders/voter-data/queue
     */
    voterDataQueue: () => client.get<VoterDataQueueResponse>('/api/work-orders/voter-data/queue'),

    /**
     * Fulfils a Paid Voter data order: a multipart upload of the CSV exported from L2 with the
     * delivered count, moving it Paid → Complete. Platform admins only; the server refuses a file
     * without the L2 voter ID and coordinates, and a count more than 5% off without
     * `customerContacted`.
     * POST /api/work-orders/{id}/fulfil
     */
    fulfilVoterData: (id: string, body: FulfilVoterDataOrderBody) => {
      const formData = new FormData();
      formData.append('file', body.file);
      formData.append('deliveredCount', String(body.deliveredCount));
      formData.append('customerContacted', String(body.customerContacted));
      formData.append('expectedConcurrencyVersion', String(body.expectedConcurrencyVersion));
      return client.request<WorkOrderDto>(`/api/work-orders/${id}/fulfil`, {
        method: 'POST',
        body: formData,
      });
    },

    /**
     * Signs a fresh short-lived link to a Complete Voter data order's file — a new one on every
     * call. 409 before the file is delivered; 410 once it has expired.
     * GET /api/work-orders/{id}/voter-data/download
     */
    voterDataDownload: (id: string) =>
      client.get<VoterDataDownloadLinkDto>(`/api/work-orders/${id}/voter-data/download`),

    /**
     * Attaches (or replaces) a Complete Text send's results report PDF. Platform admins only; the server
     * refuses a file that isn't a PDF by its bytes. Only the first report emails the campaign. Returns the
     * order with its bumped token.
     * POST /api/work-orders/{id}/results-report
     */
    attachResultsReport: (id: string, file: File, expectedConcurrencyVersion: number) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('expectedConcurrencyVersion', String(expectedConcurrencyVersion));
      return client.request<WorkOrderDto>(`/api/work-orders/${id}/results-report`, {
        method: 'POST',
        body: formData,
      });
    },

    /**
     * Signs a fresh short-lived link to a Text send's results report, a new one on every call. 409
     * `ResultsReportNotReady` before one is attached.
     * GET /api/work-orders/{id}/results-report
     */
    resultsReport: (id: string) => client.get<ResultsReportLinkDto>(`/api/work-orders/${id}/results-report`),
  };
}
